import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';
import {
  CcmPCCConsentDTO,
  NameDTO,
  PatientPCCConsentsDTO,
  PCCConsentFlagDTO,
} from 'dtos';
import {
  CcmToggleState,
  PCCForm,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/types';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import {
  formatDate,
} from 'utils/date';

const emptyCcmToggleState: CcmToggleState = {
  isEligible: false,
  isOptedOut: false,
};

export const defaultPCCFormValues: PCCForm = {
  ccm: emptyCcmToggleState,
  hasConsentedToCommunication: false,
};

function normalizeCcmConsentFlags(consent?: CcmPCCConsentDTO): CcmToggleState {
  return {
    isEligible: Boolean(consent?.eligibility?.isEnabled),
    isOptedOut: Boolean(consent?.optOut?.isEnabled),
  };
}

export function normalizePCCConsents(data?: PatientPCCConsentsDTO): PCCForm {
  return {
    hasConsentedToCommunication: Boolean(data?.consent?.isEnabled),
    ccm: normalizeCcmConsentFlags(data?.ccm),
  };
}

export function formatConsentUpdatedByName(updatedBy?: NameDTO): string | null {
  const { firstName, lastName } = updatedBy ?? {};
  const fullName = compact([firstName, lastName]).join(' ').trim();

  return (isNil(updatedBy) || isEmptyString(fullName))
    ? null
    : fullName;
}

function formatSource(source?: string | null): string | null {
  if (isEmptyString(source ?? '')) {
    return null;
  }

  return startCase(source ?? '');
}

function formatConsentDateValue(value?: string | null): string | null {
  if (isNil(value) || isEmptyString(value)) {
    return null;
  }

  return formatDate(value);
}

export function formatCompactEligibilityMetadata(consent?: CcmPCCConsentDTO): string {
  const source = formatSource(consent?.eligibility?.source);

  return notEmptyString(source) ? `Source: ${source}` : '';
}

export function formatCompactConsentMetadata(
  consent?: PCCConsentFlagDTO,
  consentGivenByName: string | null = null,
): string {
  const date = formatConsentDateValue(consent?.updatedOn);
  const source = formatSource(consent?.source);
  const givenParts = compact([
    date ? `Given ${date}` : null,
    consentGivenByName ? `by ${consentGivenByName}` : null,
  ]).join(' ');

  if (!isEmptyString(givenParts) && source) {
    return `${givenParts} · ${source}`;
  }

  if (!isEmptyString(givenParts)) {
    return givenParts;
  }

  if (source) {
    return source;
  }

  return '';
}

export function formatCompactOptOutMetadata(consent?: CcmPCCConsentDTO): string {
  const date = formatConsentDateValue(consent?.optOut?.updatedOn);
  const source = formatSource(consent?.optOut?.source);

  return (isNil(date) && isNil(source)) ? '' : compact([
    date ? `Opted out ${date}` : null,
    source,
  ]).join(' · ');
}
